import { useState, useEffect, useCallback, useRef } from "react";
import { Session, User } from "@supabase/supabase-js";
import { getSupabaseClient } from "../../config/supabase";
import { AuthStorage, SubscriptionData, fetchSubscriptionData } from "../../services/authService";
import { showToast } from "../utils/toast";
import { openAuthDialog, handleDialogMessage } from "../utils/authentication";
import { AuthenticationSession } from "../types/Authentication";

export interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  error: string | null;
  subscription: SubscriptionData | null;
}

export interface UseAuthReturn extends AuthState {
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
  refreshSubscription: () => Promise<void>;
}

/**
 * Custom hook to manage Supabase authentication in the Word Add-in
 */
export function useAuth(): UseAuthReturn {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    loading: true,
    error: null,
    subscription: null,
  });

  // Initialize auth state from storage
  useEffect(() => {
    initializeAuth();
  }, []);

  // Set up auth state change listener
  useEffect(() => {
    const supabase = getSupabaseClient();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log("Auth state changed:", event, session?.user?.email);

      if (session) {
        await AuthStorage.saveSession(session);
        setState((current) => ({
          ...current,
          user: session.user,
          session: session,
          loading: false,
          error: null,
        }));
      } else {
        await AuthStorage.clearSession();
        setState({
          user: null,
          session: null,
          loading: false,
          error: null,
          subscription: null,
        });
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  /**
   * Check if session expires within 5 minutes (300000 ms)
   */
  const checkSessionExpiration = (expiresAt: number): boolean => {
    const now = Date.now();
    const expiresAtMs = expiresAt * 1000;
    const fiveMinutesInMs = 5 * 60 * 1000; // 5 minutes
    return now >= expiresAtMs - fiveMinutesInMs;
  };

  /**
   * Refresh session if it expires within 5 minutes
   */
  const refreshSessionIfNeeded = async (session: Session): Promise<Session | null> => {
    if (checkSessionExpiration(session.expires_at)) {
      try {
        const supabase = getSupabaseClient();
        const { data, error } = await supabase.auth.refreshSession({
          refresh_token: session.refresh_token,
        });

        if (error) {
          throw error;
        }

        if (data.session) {
          try {
            await AuthStorage.saveSession(data.session);
          } catch (storageError) {
            // Storage failure - log but continue with session
            console.error("Failed to save refreshed session to storage:", storageError);
            // Session is still valid, just not persisted
          }
          return data.session;
        }
      } catch (error) {
        console.error("Failed to refresh session:", error);
        // Clear invalid session (best effort, continue even if storage fails)
        try {
          await AuthStorage.clearSession();
        } catch (storageError) {
          console.error("Failed to clear session from storage:", storageError);
        }
        return null;
      }
    }
    return session;
  };

  const initializeAuth = async () => {
    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      // Try to restore session from storage
      const savedSession = await AuthStorage.getSession();

      if (savedSession) {
        const supabase = getSupabaseClient();

        // Check if session needs refresh (expires within 5 minutes)
        const refreshedSession = await refreshSessionIfNeeded(savedSession);

        if (!refreshedSession) {
          // Session refresh failed or expired, clear and return unauthenticated
          setState({
            user: null,
            session: null,
            loading: false,
            error: null,
            subscription: null,
          });
          return;
        }

        // Set session in Supabase client
        const { data, error } = await supabase.auth.setSession({
          access_token: refreshedSession.access_token,
          refresh_token: refreshedSession.refresh_token,
        });

        if (error) {
          console.error("Failed to restore session:", error);
          await AuthStorage.clearSession();
          setState({
            user: null,
            session: null,
            loading: false,
            error: null,
            subscription: null,
          });
          return;
        }

        if (data.session) {
          await AuthStorage.saveSession(data.session);
          const subscriptionData = await fetchSubscriptionData(data.session.user.id);
          setState({
            user: data.session.user,
            session: data.session,
            loading: false,
            error: null,
            subscription: subscriptionData,
          });
        } else {
          setState({
            user: null,
            session: null,
            loading: false,
            error: null,
            subscription: null,
          });
        }
      } else {
        setState({
          user: null,
          session: null,
          loading: false,
          error: null,
          subscription: null,
        });
      }
    } catch (error) {
      console.error("Failed to initialize auth:", error);
      setState({
        user: null,
        session: null,
        loading: false,
        error: error instanceof Error ? error.message : "Failed to initialize authentication",
        subscription: null,
      });
    }
  };

  // Track active dialog to prevent duplicates
  const activeDialogRef = useRef<Office.Dialog | null>(null);

  const signIn = useCallback(async () => {
    // Prevent duplicate dialogs
    if (activeDialogRef.current) {
      console.warn("Authentication dialog is already open");
      return;
    }

    // Prevent concurrent sign-in attempts
    if (state.loading) {
      console.warn("Authentication is already in progress");
      return;
    }

    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      // Determine dialog URL based on environment
      const isDev =
        window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
      const dialogUrl = isDev
        ? "https://localhost:3000/auth-dialog.html"
        : "https://extension.my-typo.com/auth-dialog.html";

      // Open authentication dialog
      const dialog = await openAuthDialog(
        dialogUrl,
        (message) => {
          // Handle message from dialog
          handleDialogMessage(
            message,
            dialog,
            async (session: AuthenticationSession) => {
              // Clear active dialog reference
              activeDialogRef.current = null;
              // Success: Store session and update state
              try {
                const supabase = getSupabaseClient();

                // Set session in Supabase client
                await supabase.auth.setSession({
                  access_token: session.access_token,
                  refresh_token: session.refresh_token,
                });

                // Save session to storage
                const supabaseSession: Session = {
                  access_token: session.access_token,
                  refresh_token: session.refresh_token,
                  expires_at: session.expires_at,
                  expires_in: session.expires_at - Math.floor(Date.now() / 1000),
                  token_type: "bearer",
                  user: {
                    id: session.user.id,
                    email: session.user.email || undefined,
                    app_metadata: {},
                    user_metadata: {
                      full_name: session.user.name || undefined,
                    },
                    aud: "authenticated",
                    created_at: new Date().toISOString(),
                  },
                } as Session;

                try {
                  await AuthStorage.saveSession(supabaseSession);
                } catch (storageError) {
                  // Storage failure - log but continue (session is still valid)
                  console.error("Failed to save session to storage:", storageError);
                  // Session is still set in Supabase client, just not persisted
                }

                // Fetch subscription data
                const subscriptionData = await fetchSubscriptionData(session.user.id);

                // Update state
                setState({
                  user: supabaseSession.user,
                  session: supabaseSession,
                  loading: false,
                  error: null,
                  subscription: subscriptionData,
                });
              } catch (error) {
                console.error("Error processing authentication session:", error);
                setState((prev) => ({
                  ...prev,
                  loading: false,
                  error:
                    error instanceof Error ? error.message : "Failed to process authentication",
                }));
              }
            },
            (error: string) => {
              // Error: Set error state
              activeDialogRef.current = null;
              setState((prev) => ({
                ...prev,
                loading: false,
                error: error,
              }));
            }
          );
        },
        (error) => {
          // Dialog closed or cancelled
          activeDialogRef.current = null;
          // Check for cancellation error code (12006)
          const errorCode = (error as any)?.error?.code || (error as any)?.error;
          if (errorCode === 12006) {
            // User cancelled - not an error
            setState((prev) => ({
              ...prev,
              loading: false,
              error: null,
            }));
          } else {
            setState((prev) => ({
              ...prev,
              loading: false,
              error: (error as any)?.error?.message || "Authentication dialog was closed",
            }));
          }
        }
      );

      // Store dialog reference
      activeDialogRef.current = dialog;
    } catch (err) {
      activeDialogRef.current = null;
      console.error("Sign-in failed:", err);
      const errorMessage =
        err instanceof Error ? err.message : "Failed to open authentication dialog";
      setState((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
    }
  }, [state.loading]);

  const signOut = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      const supabase = getSupabaseClient();
      await supabase.auth.signOut();
      await AuthStorage.clearSession();

      setState({
        user: null,
        session: null,
        loading: false,
        error: null,
        subscription: null,
      });
    } catch (error) {
      console.error("Sign out failed:", error);
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : "Sign out failed",
      }));
    }
  }, []);

  const refreshSession = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      const supabase = getSupabaseClient();

      // First, check if there's a current session
      const {
        data: { session: currentSession },
      } = await supabase.auth.getSession();

      // If no session exists, try to restore from storage
      if (!currentSession) {
        const savedSession = await AuthStorage.getSession();
        if (savedSession) {
          // Check if session needs refresh
          const refreshedSession = await refreshSessionIfNeeded(savedSession);
          if (!refreshedSession) {
            // Refresh failed, clear and return unauthenticated
            await AuthStorage.clearSession();
            setState({
              user: null,
              session: null,
              loading: false,
              error: null,
              subscription: null,
            });
            return;
          }

          // Restore the session
          const { data: restoreData, error: restoreError } = await supabase.auth.setSession({
            access_token: refreshedSession.access_token,
            refresh_token: refreshedSession.refresh_token,
          });

          if (restoreError) {
            console.error("Failed to restore session:", restoreError);
            await AuthStorage.clearSession();
            setState({
              user: null,
              session: null,
              loading: false,
              error: null,
              subscription: null,
            });
            return;
          }

          if (restoreData.session) {
            await AuthStorage.saveSession(restoreData.session);
            const subscriptionData = await fetchSubscriptionData(restoreData.session.user.id);
            setState({
              user: restoreData.session.user,
              session: restoreData.session,
              loading: false,
              error: null,
              subscription: subscriptionData,
            });
            return;
          }
        }

        // No session available at all
        await AuthStorage.clearSession();
        setState({
          user: null,
          session: null,
          loading: false,
          error: null,
          subscription: null,
        });
        return;
      }

      // Session exists, try to refresh it
      const { data, error } = await supabase.auth.refreshSession();

      if (error) {
        throw error;
      }

      if (data.session) {
        await AuthStorage.saveSession(data.session);
        const subscriptionData = await fetchSubscriptionData(data.session.user.id);
        setState({
          user: data.session.user,
          session: data.session,
          loading: false,
          error: null,
          subscription: subscriptionData,
        });
      } else {
        await AuthStorage.clearSession();
        setState({
          user: null,
          session: null,
          loading: false,
          error: null,
          subscription: null,
        });
      }
    } catch (error) {
      console.error("Session refresh failed:", error);
      // Clear session on refresh failure and set unauthenticated state
      await AuthStorage.clearSession();
      setState({
        user: null,
        session: null,
        loading: false,
        error:
          error instanceof Error ? error.message : "Session refresh failed. Please sign in again.",
        subscription: null,
      });
    }
  }, []);

  const refreshSubscription = useCallback(async () => {
    if (!state.user) return;

    try {
      const subscriptionData = await fetchSubscriptionData(state.user.id);
      setState((prev) => ({
        ...prev,
        subscription: subscriptionData,
      }));
    } catch (error) {
      console.error("Failed to refresh subscription:", error);
    }
  }, [state.user]);

  return {
    ...state,
    signIn,
    signOut,
    refreshSession,
    refreshSubscription,
  };
}
